<template>
  <div class="customer-directory">
    <div class="customer-toolbar">
      <div class="customer-toolbar-heading">
        <TableTitle icon="account-outline" title="Customers" />
      </div>
      <div class="customer-filters">
        <label class="customer-filter">Status
          <select :value="active" class="form-control" @change="onChangeActive(($event.target as HTMLSelectElement).value)">
            <option value="1">Active customers</option><option value="0">Archived customers</option>
          </select>
        </label>
        <label class="customer-filter">Pricing
          <select :value="customPrices ?? ''" class="form-control" @change="onChangeCustomPrices(($event.target as HTMLSelectElement).value || null)">
            <option value="">All pricing</option><option value="1">Custom prices</option><option value="0">Standard prices</option>
          </select>
        </label>
        <div class="customer-search"><span>Search customers</span><SearchField @search-triggered="updateSearch" /></div>
        <router-link class="btn btn-green customer-add" :to="createCustomerPage">
          <mdicon name="plus" size="18" aria-hidden="true" /> Add customer
        </router-link>
      </div>
    </div>
    <easy-data-table
      ref="dataTable"
      v-model:server-options="serverOptions"
      table-class-name="vue3-easy-data-table table-shadow no-overflow"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :headers="headers"
      :items="items"
      :prevent-context-menu-row="false"
      :fixed-header="false"
      no-hover
      alternating
      hide-footer
      @update:server-options="updateServerOptions"
    >
      <template #loading>
        <b-spinner class="mt-5" variant="success" />
      </template>
      <template #empty-message><div class="customer-empty"><strong>No customers found</strong><p>Try another search or change the filters.</p></div></template>
      <template #item="{ column, item }: { column: string; item: Company }">
        <span v-if="column === 'name'" class="customer-list-name">{{ item.name }}</span>
        <span v-else-if="column === 'clientAddress.addressLine'">
          <div class="address-line">
            {{ ucFirst(item.clientAddress.addressLine) }}
          </div>
        </span>
        <span v-else-if="column === 'senderAddress.addressLine'">
          <div class="address-line">
            {{ ucFirst(item.senderAddress.addressLine) }}
          </div>
        </span>
        <span v-else-if="column === 'contactName'">
          <div>
            {{ item.contactName ? ucFirst(`${item.contactName} ${item.contactLastName || ''}`) : '—' }}
            <small v-if="item.phoneNumber" class="customer-list-secondary">{{ item.phoneNumber }}</small>
          </div>
        </span>
        <span v-else-if="column === 'actions'" class="d-flex">
          <b-dropdown
            v-if="item.isActive"
            text="Actions"
            :toggle-text="`Actions for ${item.name}`"
            variant="outline-secondary"
            size="sm"
            placement="bottom-end"
            lazy
            class="customer-actions-menu"
          >
            <b-dropdown-item @click="authAsCompany(item)">
              <span class="d-flex align-items-center">
                <mdicon class="mr-2" name="account-arrow-left" size="18" />
                <span>Log in as customer</span>
              </span>
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item @click="viewAssignments(item.id)">
              <span class="d-flex align-items-center">
                <mdicon class="pointer mr-2" name="file-document-multiple-outline" size="18" />
                <span>View assignments</span>
              </span>
            </b-dropdown-item>
            <b-dropdown-item @click="viewShipments(item.id)">
              <span class="d-flex align-items-center">
                <i class="fa mr-2 w-18 fa-truck" />
                <span> View shipments</span>
              </span>
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item v-if="canViewInventory" @click="manageInventory(item.id)">
              <span class="d-flex align-items-center">
                <mdicon class="pointer mr-2" name="PackageVariantClosed" size="18" />
                <span>Manage inventory</span>
              </span>
            </b-dropdown-item>
            <b-dropdown-item @click="manageAddressBook(item.id)">
              <span class="d-flex align-items-center">
                <mdicon class="pointer mr-2" name="book-open-outline" size="18" />
                <span>Address book</span>
              </span>
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item variant="danger" @click="archiveCompany(item.id)">
              <span class="d-flex align-items-center">
                <mdicon class="pointer mr-2" name="archive-outline" size="18" />
                <span> Archive customer</span>
              </span>
            </b-dropdown-item>
          </b-dropdown>
          <b-button v-if="!item.isActive" class="h-32" variant="secondary" size="sm" @click="unarchiveCompany(item.id)">
            Unarchive
          </b-button>
        </span>
        <span v-else>
          {{ item[column] }}
        </span>
      </template>
      <template #expand="item">
        <ChildRowCompanyData :company="item" />
      </template>
    </easy-data-table>

    <TablePagination
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="serverItemsLength"
    />
    <confirm
      id="confirmDelete"
      message="The client will become inactive, he will not be able to log in and have assignments. Are you sure you want to archive client?"
      title="Archive client"
      :on-confirm="onArchive"
    />
  </div>
</template>

<script lang="ts">
import type { RouterLink } from '@/types/Components/Router'
import type { SelectOptions } from '@/types/Components/SelectOptions'
import type { Company } from '@/types/Models/Company'
import type { UsersModal } from '@/types/Models/User'
import { BButton, BDropdown, BDropdownDivider, BDropdownItem, BSpinner } from 'bootstrap-vue-next'
import { computed, defineComponent, onMounted, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { CUSTOMER } from '@/const/AccountTypes'
import { USERS } from '@/const/ModalTypes'
import { none, ucFirst } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useAccountStore } from '@/store/account'
import { useAuthStore } from '@/store/auth'
import { useCustomersStore } from '@/store/customers'
import {
  STAFF_ADDRESS_BOOK_COMPANY,
  STAFF_ASSIGNMENTS_LIST,
  STAFF_CUSTOMERS_CREATE,
  STAFF_INVENTORY_PRODUCTS,
  STAFF_SHIPMENTS,
} from '@/types/router'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import Confirm from '@/views/Modals/Confirm.vue'
import TableTitle from '@/views/Components/Elements/Table/TableTitle.vue'
import SearchField from '@/views/Components/PageParts/Search.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'
import ChildRowCompanyData from '@/views/Components/Customer/ChildRowCompanyData.vue'

export default defineComponent({
  name: 'ActiveCompaniesTable',
  components: {
    BButton,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    ChildRowCompanyData,
    TablePagination,
    EasyDataTable,
    SearchField,
    TableTitle,
    Confirm,
  },
  props: {
    refresh: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const router = useRouter()
    const authStore = useAuthStore()
    const accountStore = useAccountStore()
    const customerStore = useCustomersStore()

    const canViewInventory = computed(() => accountStore.ability.can('view' as any, 'inventory' as any))

    const createCustomerPage: RouterLink = { name: STAFF_CUSTOMERS_CREATE }

    const dataTable: Ref = ref()
    const searchText: Ref<string> = ref('')
    const serverOptions: Ref<ServerOptions> = ref({
      page: 1,
      rowsPerPage: 10,
      sortBy: 'createdAt',
      sortType: 'desc',
    })

    const items: ComputedRef<Company[]> = computed(() => customerStore.listCustomers ?? [])
    const loading: ComputedRef<boolean> = computed(() => customerStore.loading)
    const serverItemsLength: ComputedRef<number> = computed(() => customerStore.count)
    const refresh: ComputedRef<string> = computed(() => props.refresh)
    const active: ComputedRef<number> = computed(() => Number(customerStore.activeFilter ?? 1))
    const customPrices: ComputedRef<string | null> = computed(() => {
      const value = customerStore.hasCustomPricesFilter
      return value === null || value === undefined ? null : String(value)
    })

    const setCustomersFilter = async () => {
      customerStore
        .setFilterAndReload({
          page: serverOptions.value.page,
          limit: serverOptions.value.rowsPerPage,
          ascending: serverOptions.value.sortType === 'asc' ? 1 : 0,
          orderBy: String(serverOptions.value.sortBy || 'createdAt'),
          active: active.value,
          type: CUSTOMER,
          query: searchText.value,
          hasCustomPrices: customPrices.value === null ? null : Number(customPrices.value),
        })
        .then(none)
    }

    const updateSearch = (value: string) => {
      searchText.value = value
    }
    const updateServerOptions = (newOptions: ServerOptions) => {
      serverOptions.value = newOptions
    }

    const headers: Header[] = [
      { value: 'name', text: 'Company' },
      { value: 'email', text: 'Email address', sortable: true },
      { value: 'clientNumber', text: 'Client №', sortable: true },
      { value: 'contactName', text: 'Contact', sortable: true },

      { value: 'actions', text: '' },
    ]

    const options: SelectOptions[] = [
      { value: '1', text: 'Active' },
      { value: '0', text: 'Archived' },
    ]
    const hasCustomPrices: SelectOptions[] = [
      { value: null, text: 'Custom price' },
      { value: '1', text: 'Has custom prices' },
      { value: '0', text: 'Without custom prices' },
    ]

    const viewShipments = (id: string) => {
      router.push({
        name: STAFF_SHIPMENTS,
        query: {
          customer: [id],
        },
      })
    }

    const viewAssignments = (id: string) => {
      router.push({
        name: STAFF_ASSIGNMENTS_LIST,
        query: {
          companyId: [id],
        },
      })
    }

    const archiveCompany = (id: string) => {
      openModal('confirmDelete', { id })
    }
    const onArchive = (data: Company) => {
      customerStore.archive(data.id).then(refreshList)
    }
    const unarchiveCompany = (id: string) => {
      customerStore.unarchive(id).then(refreshList)
    }
    const openUsersList = (id: string) => {
      openModal(USERS, { companyId: id } as UsersModal)
    }
    const manageInventory = (id: string) => {
      router.push({
        name: STAFF_INVENTORY_PRODUCTS,
        params: {
          customerId: id,
        },
      })
    }
    const manageAddressBook = (id: string) => {
      router.push({
        name: STAFF_ADDRESS_BOOK_COMPANY,
        params: {
          companyId: id,
        },
      })
    }
    const authAsCompany = (row: Company) => {
      authStore.authAsCompany(row.id)
    }


    const refreshList = () => {
      customerStore.refresh()
    }
    const onChangeCustomPrices = (value: string | null) => {
      customerStore.updateFilterValue({
        key: 'hasCustomPrices',
        value: value === null ? null : Number(value),
      })
    }
    const onChangeActive = (value: string) => {
      customerStore.updateFilterValue({ key: 'active', value: Number(value) })
    }

    onMounted(setCustomersFilter)
    watch(serverOptions, setCustomersFilter)
    watch(searchText, setCustomersFilter)
    watch(refresh, refreshList)
    watch([active, searchText], () => {
      serverOptions.value.page = 1
    })

    return {
      active,
      customPrices,
      options,
      hasCustomPrices,
      createCustomerPage,
      authAsCompany,
      viewShipments,
      viewAssignments,
      unarchiveCompany,
      archiveCompany,
      onArchive,
      ucFirst,
      updateSearch,
      openUsersList,
      manageInventory,
      canViewInventory,
      manageAddressBook,
      onChangeCustomPrices,
      onChangeActive,

      //data table options
      dataTable,
      items,
      loading,
      headers,
      serverOptions,
      serverItemsLength,
      updateServerOptions,

      USERS,
    }
  },
})
</script>

<style scoped lang="scss">
a.btn-plus {
  padding: 8px !important;
  margin-left: 12px;
}
.vue3-easy-data-table thead tr th {
  padding: 0 17px !important;
}
.address-line {
  width: 160px;
  white-space: pre-line;
}
</style>

<style scoped>
.customer-actions-menu :deep(.dropdown-toggle) {
  min-height: 36px;
  white-space: nowrap;
  color: #344b47;
  background: #fff;
  border-color: #b8c4c1;
}
.customer-actions-menu :deep(.dropdown-toggle:hover),
.customer-actions-menu :deep(.dropdown-toggle.show) {
  color: #174c45;
  background: #eaf2ef;
  border-color: #648e85;
}
.customer-actions-menu :deep(.dropdown-menu) { min-width: 230px; }
.customer-actions-menu :deep(.dropdown-item) { padding: 10px 16px; }
.customer-toolbar { margin-bottom: 20px; }
.customer-toolbar-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
.customer-add { display: inline-flex; align-items: center; gap: 8px; min-height: 44px; white-space: nowrap; flex-shrink: 0; }
.customer-filters { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 16px; }
.customer-filter, .customer-search { font-size: 13px; color: #59635e; margin: 0; }
.customer-filter { display: grid; gap: 6px; min-width: 180px; }
.customer-filter select { min-height: 44px; font-size: 14px; color: #253b35; }
.customer-search { margin-left: auto; }
.customer-search > span { display: block; margin-bottom: 6px; }
.customer-empty { padding: 32px 16px; color: #59635e; }
.customer-empty strong { color: #253b35; }
.customer-empty p { margin: 8px 0 0; }
@media (max-width: 700px) {
  .customer-filter { flex: 1 1 150px; }
  .customer-search { margin-left: 0; width: 100%; }
}
.customer-list-name { font-weight: 600; }
.customer-list-secondary { display: block; margin-top: 4px; color: #59635e; }
.customer-directory :deep(.vue3-easy-data-table__main table) { min-width: 760px; }
</style>
